/**
 * Uncanned free-gift auto-add (Drop 01).
 *
 * WHY THIS EXISTS
 * Shopify's Buy-X-Get-Y automatic discount makes the UN Tote free once the soda
 * subtotal is ≥ ₹999 — but the discount engine can only zero out a line that is
 * ALREADY in the cart; it cannot add one (verified against the Admin API schema:
 * no auto-add field on DiscountAutomaticBxgyInput). So the theme does the adding;
 * the discount does the pricing.
 *
 * RULES (Drop 01 final, per the offer sheet)
 *  - TOTE: eligible subtotal ≥ threshold → add 1 tote. Codes STACK with the tote
 *    (admin combination rules: the BXGY combines with order discounts, and every
 *    live code combines with product discounts) — a pilot at ₹999+ gets code AND
 *    tote. This is the curated preorder deal.
 *  - TOTE STOCK GATE: the gift tote is tracked with a fixed run, deny-when-out.
 *    config.toteAvailable (stamped by Liquid) is the primary gate; a failed add
 *    (someone claims the last one mid-session) flips it off client-side too.
 *  - FREEBIES: every order gets a free gift. UNPILOT / UNTEST → keychain
 *    (codeGifts); everything else, with or without a code → sticker sheet
 *    (defaultGiftVariantId). Exactly one freebie line, never two.
 *  - eligible subtotal excludes all gift lines (they never fund their own threshold)
 *  - idempotent + loop-safe: every add/remove republishes cart:updated, which
 *    re-runs this; the guards make the second pass a no-op.
 *
 * CONFIG comes from window.UncannedFreeGift (stamped by the header section):
 * { enabled, variantId, threshold (paise), toteAvailable, giftVariantIds,
 *   codeGifts: {CODE: variantId}, defaultGiftVariantId, removeGiftWhenCodeRemoved }.
 */
(function () {
  'use strict';

  // FAILSAFE DEFAULTS. The header's inline config has been silently lost THREE
  // times by theme copies/version restores resurrecting a Liquid whitespace bug
  // (real orders shipped without their free gifts each time). These defaults
  // mirror the header config exactly; anything the header DOES stamp wins.
  var GIFT_DEFAULTS = {
    enabled: true,
    toteAuto: false,                     // never auto-add/remove the tote unless the header says so
    giftDefaultAuto: false,              // free gift with every order: off unless the header says so
    giftCodeAuto: false,                 // free gift for specific codes: off unless the header says so
    variantId: 49184572375284,          // UN Tote Bag
    threshold: 99900,                    // paise = Rs.999
    toteAvailable: true,
    giftVariantIds: [49184572375284, 49182129946868, 49182148624628],
    codeGifts: { UNPILOT: 49182129946868, UNTEST: 49182129946868, PREORDER75: 49182148624628 },  // pilot→keychain, preorder→sticker
    defaultGiftVariantId: 49182148624628,                            // sticker
    removeGiftWhenCodeRemoved: true
  };
  window.UncannedFreeGift = Object.assign({}, GIFT_DEFAULTS, window.UncannedFreeGift || {});

  var busy = false;

  function cfg() {
    var c = window.UncannedFreeGift || {};
    var vid = parseInt(c.variantId, 10);
    var threshold = parseInt(c.threshold, 10);
    if (!c.enabled || !vid || !threshold) return null;
    return { vid: vid, threshold: threshold };
  }

  function isGift(item) {
    // cart.js line items don't carry product tags reliably, so the header stamps the
    // gift variant ids into config; anything in that list is excluded from the count.
    var gifts = (window.UncannedFreeGift && window.UncannedFreeGift.giftVariantIds) || [];
    return gifts.indexOf(item.variant_id) !== -1;
  }

  function appliedCodes(cart) {
    // Order-level applications only. Shipping discounts and the subscription app's
    // automatic (UNSUBS) are not "codes" for tote purposes.
    return (cart.cart_level_discount_applications || [])
      .map(function (d) { return String(d.title || '').toUpperCase().trim(); })
      .filter(function (t) { return t && t.indexOf('SHIPPING') === -1 && t.indexOf('UNSUBS') === -1; });
  }

  function reconcile(cart) {
    var c = cfg();
    if (!c || !cart || busy) return;

    var client = window.Uncanned && window.Uncanned.cart;
    if (!client) return;

    var conf = window.UncannedFreeGift;
    var toteLine = null;
    var eligible = 0;
    (cart.items || []).forEach(function (item) {
      if (item.variant_id === c.vid) {
        toteLine = item;
      } else if (!isGift(item)) {
        eligible += item.final_line_price;
      }
    });

    var codes = appliedCodes(cart);
    var toteAvailable = conf.toteAvailable !== false;
    // Drop 01 reward chooser: 'cash' means the customer picked ₹100 off
    // instead of the tote — never auto-add against that choice.
    var rewardChoice = String((cart.attributes && cart.attributes.drop01_reward) || '').toLowerCase();
    var qualifies = toteAvailable && eligible >= c.threshold && rewardChoice !== 'cash';
    // The tote is sold as an add-on now: only touch it when the header explicitly
    // turns the old free-tote automation back on.
    var toteAuto = conf.toteAuto === true;

    if (toteAuto && qualifies && !toteLine) {
      busy = true;
      client
        .add({ id: c.vid, quantity: 1 }, { source: 'free_gift_auto', openDrawer: false })
        .catch(function (err) {
          // Add refused — the stock gate (last tote just went), or the tote
          // product is not addable at all (draft / unpublished / sold out).
          // Flip the flag so we stop retrying; the drawer's next Liquid render
          // shows the "all claimed" banner. The warn makes this diagnosable.
          console.warn(
            '[Uncanned free gift] Tote auto-add was refused by Shopify. Check that the tote product is Active, published to the Online Store channel, and in stock (variant ' + c.vid + ').',
            err
          );
          conf.toteAvailable = false;
        })
        .then(function () { busy = false; });
      return;
    }

    if (toteAuto && !qualifies && toteLine) {
      busy = true;
      client
        .remove(toteLine.key, { source: 'free_gift_auto' })
        .catch(function () {})
        .then(function () { busy = false; });
      return;
    }

    // ---- freebies: keychain for pilot codes, sticker sheet for everyone else ----
    var codeGifts = conf.codeGifts || {};
    var defaultGift = parseInt(conf.defaultGiftVariantId, 10) || null;
    var wantedGift = null;
    // Both gift programmes are opt-in from the header. Switched off, nothing is
    // wanted, so the branch below also clears a gift left in the cart from before.
    if (conf.giftCodeAuto === true) {
      Object.keys(codeGifts).forEach(function (code) {
        if (codes.indexOf(code) !== -1) wantedGift = parseInt(codeGifts[code], 10);
      });
    }
    // No pilot code → the default gift, but only when there's a real item to gift on.
    if (!wantedGift && conf.giftDefaultAuto === true && defaultGift && eligible > 0) wantedGift = defaultGift;

    var giftVids = Object.keys(codeGifts).map(function (k) { return parseInt(codeGifts[k], 10); });
    if (defaultGift && giftVids.indexOf(defaultGift) === -1) giftVids.push(defaultGift);
    var giftLines = (cart.items || []).filter(function (i) { return giftVids.indexOf(i.variant_id) !== -1; });

    if (wantedGift) {
      var have = giftLines.filter(function (i) { return i.variant_id === wantedGift; })[0];
      // Wrong freebies (the default gift landing before the code did) leave in the
      // SAME busy window as the add: splitting them across two reconcile passes left
      // a gap where checkout froze BOTH gifts into a real order (#UN1054). The add
      // runs first so an interrupted pilot still holds the keychain, and the removes
      // only run once the add succeeded — a refused add must not strip the only gift.
      var strays = giftLines.filter(function (i) { return i.variant_id !== wantedGift; });
      if (!have || strays.length) {
        busy = true;
        var op = have
          ? Promise.resolve()
          : client.add({ id: wantedGift, quantity: 1 }, { source: 'free_gift_code', openDrawer: false });
        strays.forEach(function (line) {
          op = op.then(function () { return client.remove(line.key, { source: 'free_gift_code' }); });
        });
        op.catch(function () {}).then(function () { busy = false; });
        return;
      }
    } else if (giftLines.length) {
      // Cart emptied of real items → freebies go too; nobody checks out only ₹0 lines.
      busy = true;
      client.remove(giftLines[0].key, { source: 'free_gift_code' }).catch(function () {}).then(function () { busy = false; });
      return;
    }

    if (toteAuto && toteLine && toteLine.quantity > 1) {
      // Belt and braces: the drawer already caps gift qty at 1, but a permalink or
      // another script could push it higher. The discount only zeroes one.
      busy = true;
      client
        .change(toteLine.key, 1, { source: 'free_gift_auto' })
        .catch(function () {})
        .then(function () { busy = false; });
    }
  }

  document.addEventListener('cart:updated', function (event) {
    reconcile(event.detail && event.detail.cart);
  });

  // First paint: the client may already hold a snapshot; otherwise fetch once.
  function boot() {
    if (!cfg()) return;
    if (window.Uncanned && window.Uncanned.cartSnapshot) {
      reconcile(window.Uncanned.cartSnapshot);
      return;
    }
    fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (r) { return r.json(); })
      .then(reconcile)
      .catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
